/**
 * Cart Drawer - slide-out panel for the calculator-based cart.
 *
 * Uses localStorage to persist the Shopify Draft Order ID across page
 * navigations. Items are fetched from the VPS backend (GET /cart) and
 * managed via POST /cart/add and POST /cart/remove.
 *
 * Exposes a global `window.TECart` object so the calculator can interact
 * with the cart (add items, update the badge, open the drawer).
 */
(function () {
  'use strict';

  // ── Configuration ────────────────────────────────────────────────────────
  var STORAGE_KEY = 'te_cart';
  var STORAGE_TTL = 7 * 24 * 60 * 60 * 1000; // 7 days
  var EMAIL_KEY = 'te_email';
  var EMAIL_RE = /^[^\s@<>()[\]\\,;:"]+@[^\s@<>()[\]\\,;:"]+\.[A-Za-z]{2,}$/;

  // The API base is injected by the Liquid snippet as a data attribute
  // on the drawer element. Falls back to empty (same-origin, dev only).
  var drawerEl = document.getElementById('te-cart-drawer');
  if (!drawerEl) return;
  var API_BASE = drawerEl.getAttribute('data-api-base') || '';
  var CCY = drawerEl.getAttribute('data-currency') || '£';
  var CUSTOMER_ID = drawerEl.getAttribute('data-customer-id') || '';
  var CUSTOMER_EMAIL = drawerEl.getAttribute('data-customer-email') || '';
  var LOGIN_URL = drawerEl.getAttribute('data-login-url') || '/account/login';

  // ── LocalStorage helpers ─────────────────────────────────────────────────
  function getStored() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var data = JSON.parse(raw);
      if (Date.now() - data.ts > STORAGE_TTL) {
        localStorage.removeItem(STORAGE_KEY);
        return null;
      }
      return data;
    } catch (e) {
      return null;
    }
  }

  function setStored(draftOrderId, itemCount) {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ id: draftOrderId, count: itemCount, ts: Date.now() })
      );
    } catch (e) {
      // Storage full or unavailable - cart will still work for this session.
    }
  }

  function clearStored() {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (e) { /* noop */ }
  }

  // The email box remembers what was typed, so a returning shopper (or a re-render
  // after changing delivery) never has to type it twice.
  function getStoredEmail() {
    try {
      return localStorage.getItem(EMAIL_KEY) || '';
    } catch (e) {
      return '';
    }
  }

  function setStoredEmail(email) {
    try {
      localStorage.setItem(EMAIL_KEY, email);
    } catch (e) { /* noop */ }
  }

  // ── DOM references ───────────────────────────────────────────────────────
  var overlay = drawerEl.querySelector('.cart-drawer__overlay');
  var closeBtn = drawerEl.querySelector('.cart-drawer__close');
  var bodyEl = drawerEl.querySelector('.cart-drawer__body');
  var footEl = drawerEl.querySelector('.cart-drawer__foot');
  var titleCountEl = drawerEl.querySelector('.cart-drawer__count-badge');
  var headerBadge = document.querySelector('[data-cart-count]');

  // ── Drawer open/close ────────────────────────────────────────────────────
  var isOpen = false;

  function openDrawer() {
    isOpen = true;
    drawerEl.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    loadCart();
  }

  function closeDrawer() {
    isOpen = false;
    drawerEl.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  overlay.addEventListener('click', closeDrawer);
  closeBtn.addEventListener('click', closeDrawer);
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && isOpen) closeDrawer();
  });

  // Make the header cart link open the drawer instead of navigating.
  var cartLink = document.querySelector('a[href*="/cart"]');
  if (cartLink && cartLink.closest('.header__actions')) {
    cartLink.addEventListener('click', function (e) {
      e.preventDefault();
      openDrawer();
    });
  }

  // ── Badge update ─────────────────────────────────────────────────────────
  function updateBadge(count) {
    if (headerBadge) {
      headerBadge.textContent = count;
      headerBadge.style.display = count > 0 ? '' : 'none';
    }
    if (titleCountEl) {
      titleCountEl.textContent = count;
      titleCountEl.style.display = count > 0 ? '' : 'none';
    }
  }

  // Restore badge from localStorage on page load.
  var stored = getStored();
  if (stored && stored.count > 0) {
    updateBadge(stored.count);
  } else {
    updateBadge(0);
  }

  // Buy Again / reorder / invoice landing:
  //   ?te_reorder=<orderId>  → POST /cart/reorder (direct VPS), then open drawer
  //   ?te_invoice=<orderId>  → POST /invoice/download, save VAT PDF
  //   ?te_cart=<draftId>&te_open=1  → adopt an already-built draft (app-proxy path)
  (function adoptReorderCart() {
    try {
      var params = new URLSearchParams(window.location.search);
      var reorderOrderId = params.get('te_reorder');
      var invoiceOrderId = params.get('te_invoice');
      var draftId = params.get('te_cart');

      function stripParams(keys) {
        keys.forEach(function (k) { params.delete(k); });
        var clean = window.location.pathname + (params.toString() ? '?' + params.toString() : '') + window.location.hash;
        if (window.history && window.history.replaceState) {
          window.history.replaceState({}, '', clean);
        }
      }

      function requireLogin(param, orderId) {
        window.location.href = LOGIN_URL + (LOGIN_URL.indexOf('?') >= 0 ? '&' : '?') +
          'return_url=' + encodeURIComponent(window.location.pathname + '?' + param + '=' + encodeURIComponent(orderId));
      }

      if (invoiceOrderId) {
        stripParams(['te_invoice', 'te_reorder', 'te_cart', 'te_open', 'te_count']);
        if (!CUSTOMER_ID) {
          requireLogin('te_invoice', invoiceOrderId);
          return;
        }
        if (!API_BASE) {
          console.error('TECart: calculator API URL is not configured.');
          return;
        }
        fetch(API_BASE.replace(/\/$/, '') + '/invoice/download', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ orderId: invoiceOrderId, customerId: CUSTOMER_ID }),
        })
          .then(function (r) {
            if (!r.ok) {
              return r.json().then(function (body) {
                throw new Error((body && body.error) || 'Could not download invoice.');
              });
            }
            var disposition = r.headers.get('Content-Disposition') || '';
            var match = disposition.match(/filename="([^"]+)"/i);
            var filename = (match && match[1]) || 'VAT-Invoice.pdf';
            return r.blob().then(function (blob) {
              return { blob: blob, filename: filename };
            });
          })
          .then(function (file) {
            var url = URL.createObjectURL(file.blob);
            var a = document.createElement('a');
            a.href = url;
            a.download = file.filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(function () { URL.revokeObjectURL(url); }, 2000);
            showToast('VAT invoice downloaded');
          })
          .catch(function (err) {
            console.error(err);
            alert(err.message || 'Could not download the VAT invoice.');
          });
        return;
      }

      if (reorderOrderId) {
        stripParams(['te_reorder', 'te_cart', 'te_open', 'te_count']);
        if (!CUSTOMER_ID) {
          requireLogin('te_reorder', reorderOrderId);
          return;
        }
        if (!API_BASE) {
          console.error('TECart: calculator API URL is not configured.');
          return;
        }
        openDrawer();
        renderLoading();
        fetch(API_BASE.replace(/\/$/, '') + '/cart/reorder', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ orderId: reorderOrderId, customerId: CUSTOMER_ID }),
        })
          .then(function (r) {
            return r.json().then(function (body) {
              return { ok: r.ok, status: r.status, body: body };
            });
          })
          .then(function (res) {
            if (!res.ok || !res.body.draftOrderId) {
              bodyEl.innerHTML =
                '<div class="cart-drawer__empty">' +
                '<p>Could not buy again</p>' +
                '<p class="cart-drawer__empty-sub">' +
                (res.body && res.body.error ? res.body.error : 'Please try again in a moment.') +
                '</p></div>';
              footEl.style.display = 'none';
              return;
            }
            setStored(res.body.draftOrderId, res.body.itemCount || 0);
            updateBadge(res.body.itemCount || 0);
            showToast('Order added to cart');
            loadCart();
          })
          .catch(function () {
            bodyEl.innerHTML =
              '<div class="cart-drawer__empty">' +
              '<p>Could not buy again</p>' +
              '<p class="cart-drawer__empty-sub">Please try again in a moment.</p>' +
              '</div>';
            footEl.style.display = 'none';
          });
        return;
      }

      if (!draftId) return;
      var count = parseInt(params.get('te_count') || '0', 10) || 0;
      setStored(draftId, count);
      updateBadge(count);
      var open = params.get('te_open') === '1';
      stripParams(['te_cart', 'te_open', 'te_count']);
      if (open) {
        setTimeout(function () { openDrawer(); }, 0);
      }
    } catch (e) { /* noop */ }
  })();

  // ── Load cart items ──────────────────────────────────────────────────────
  var lastCartData = null;
  var selectedDelivery = null;
  var contactEmail = CUSTOMER_EMAIL || getStoredEmail();
  var noReminder = false;
  var quoteStatus = null;

  function loadCart() {
    var data = getStored();
    if (!data || !data.id) {
      renderEmpty();
      return;
    }
    renderLoading();

    fetch(API_BASE + '/cart?id=' + encodeURIComponent(data.id))
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (cart.expired) {
          clearStored();
          updateBadge(0);
          renderEmpty();
          return;
        }
        lastCartData = cart;
        updateBadge(cart.itemCount);
        setStored(data.id, cart.itemCount);
        if (!cart.items || cart.items.length === 0) {
          renderEmpty();
        } else {
          renderItems(cart);
        }
      })
      .catch(function () {
        bodyEl.innerHTML =
          '<div class="cart-drawer__empty">' +
          '<p>Could not load your cart.</p>' +
          '<p class="cart-drawer__empty-sub">Please try again in a moment.</p>' +
          '</div>';
        footEl.style.display = 'none';
      });
  }

  // ── Render states ────────────────────────────────────────────────────────
  function renderLoading() {
    bodyEl.innerHTML =
      '<div class="cart-drawer__loading">' +
      '<div class="cart-drawer__spinner"></div>' +
      '<span>Loading cart\u2026</span>' +
      '</div>';
    footEl.style.display = 'none';
  }

  function renderEmpty() {
    bodyEl.innerHTML =
      '<div class="cart-drawer__empty">' +
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true">' +
      '<path d="M3 3h2l2.4 12.2a2 2 0 0 0 2 1.6h7.7a2 2 0 0 0 2-1.6L23 6H6"></path>' +
      '<circle cx="10" cy="20" r="1.4"></circle><circle cx="18" cy="20" r="1.4"></circle>' +
      '</svg>' +
      '<p>Your cart is empty</p>' +
      '<p class="cart-drawer__empty-sub">Configure a product and click \u201cAdd to Cart\u201d to get started.</p>' +
      '</div>';
    footEl.style.display = 'none';
  }

  function renderItems(cart) {
    bodyEl.innerHTML = '';
    var total = 0;

    cart.items.forEach(function (item) {
      var el = document.createElement('div');
      el.className = 'cart-item';

      // Image
      var imgBox = document.createElement('div');
      imgBox.className = 'cart-item__image';
      if (item.image) {
        imgBox.innerHTML = '<img src="' + item.image + '" alt="" loading="lazy">';
      } else {
        imgBox.className += ' cart-item__image--empty';
        imgBox.innerHTML =
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true">' +
          '<rect x="3" y="3" width="18" height="18" rx="2"></rect>' +
          '<circle cx="8.5" cy="8.5" r="1.5"></circle>' +
          '<path d="M21 15l-5-5L5 21"></path>' +
          '</svg>';
      }
      el.appendChild(imgBox);

      // Info
      var info = document.createElement('div');
      info.className = 'cart-item__info';

      var title = document.createElement('p');
      title.className = 'cart-item__title';
      title.textContent = item.title || 'Product';
      info.appendChild(title);

      // Build meta from custom attributes (skip internal ones).
      var metaParts = [];
      if (item.attributes) {
        Object.keys(item.attributes).forEach(function (key) {
          if (key.charAt(0) === '_') return; // skip internal attrs
          if (key === 'Stock code') return; // for picking; would push qty out of the four shown
          var val = item.attributes[key];
          if (val && val.length < 60) {
            metaParts.push(key + ': ' + val);
          }
        });
      }
      if (metaParts.length) {
        var meta = document.createElement('p');
        meta.className = 'cart-item__meta';
        meta.textContent = metaParts.slice(0, 4).join(' · ');
        info.appendChild(meta);
      }

      var price = document.createElement('p');
      price.className = 'cart-item__price';
      var priceNum = parseFloat(item.price) || 0;
      total += priceNum;
      price.textContent = CCY + priceNum.toFixed(2);
      info.appendChild(price);

      el.appendChild(info);

      // Remove button
      var remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'cart-item__remove';
      remove.setAttribute('aria-label', 'Remove ' + (item.title || 'item'));
      remove.innerHTML =
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">' +
        '<path d="M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"></path>' +
        '<path d="M10 11v6M14 11v6"></path>' +
        '</svg>';
      remove.addEventListener('click', function () {
        removeItem(item.id, el);
      });
      el.appendChild(remove);

      bodyEl.appendChild(el);
    });

    // Footer: one delivery charge for the whole order, then checkout.
    var delivery = cart.delivery || { options: [], note: '' };
    var options = delivery.options || [];
    if (!options.some(function (option) { return option.id === selectedDelivery; })) {
      selectedDelivery = options.length ? options[0].id : null;
    }
    var chosen = options.filter(function (option) { return option.id === selectedDelivery; })[0];
    var deliveryPrice = chosen ? Number(chosen.price) || 0 : 0;

    footEl.style.display = '';
    footEl.innerHTML = '';

    if (options.length) {
      var block = document.createElement('fieldset');
      block.className = 'cart-delivery';
      var legend = document.createElement('legend');
      legend.className = 'cart-delivery__label';
      legend.textContent = 'Delivery';
      block.appendChild(legend);

      if (delivery.note) {
        var note = document.createElement('p');
        note.className = 'cart-delivery__note';
        note.textContent = delivery.note;
        block.appendChild(note);
      }

      options.forEach(function (option) {
        var label = document.createElement('label');
        label.className = 'cart-delivery__option';
        var input = document.createElement('input');
        input.type = 'radio';
        input.name = 'te-delivery';
        input.value = option.id;
        input.checked = option.id === selectedDelivery;
        input.addEventListener('change', function () {
          selectedDelivery = option.id;
          renderItems(cart);
        });
        var name = document.createElement('span');
        name.className = 'cart-delivery__name';
        name.textContent = option.title;
        var price = document.createElement('span');
        price.className = 'cart-delivery__price';
        price.textContent = option.price > 0 ? CCY + Number(option.price).toFixed(2) : 'Free';
        label.appendChild(input);
        label.appendChild(name);
        label.appendChild(price);
        block.appendChild(label);
      });
      footEl.appendChild(block);
    }

    var totalRow = document.createElement('div');
    totalRow.className = 'cart-drawer__total';
    totalRow.innerHTML =
      '<span class="cart-drawer__total-label">Total</span>' +
      '<span class="cart-drawer__total-value">' + CCY + (total + deliveryPrice).toFixed(2) +
      ' <span class="cart-drawer__total-vat">Excl. VAT</span></span>';
    footEl.appendChild(totalRow);
    footEl.appendChild(renderContact());

    var checkoutBtn = document.createElement('button');
    checkoutBtn.type = 'button';
    checkoutBtn.className = 'cart-drawer__checkout';
    checkoutBtn.id = 'te-checkout-btn';
    checkoutBtn.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">' +
      '<path d="M5 12h14M13 6l6 6-6 6"></path>' +
      '</svg>' +
      'Checkout';
    checkoutBtn.addEventListener('click', function () {
      startCheckout(checkoutBtn);
    });
    footEl.appendChild(checkoutBtn);

    // For shoppers who need sign-off or a purchase order before they can pay.
    var quoteBtn = document.createElement('button');
    quoteBtn.type = 'button';
    quoteBtn.className = 'cart-drawer__quote';
    quoteBtn.textContent = 'Email me a quote';
    quoteBtn.addEventListener('click', function () {
      sendQuote(quoteBtn, total + deliveryPrice);
    });
    footEl.appendChild(quoteBtn);

    var status = document.createElement('p');
    status.className = 'cart-contact__status';
    status.id = 'te-cart-status';
    status.setAttribute('aria-live', 'polite');
    if (quoteStatus) status.textContent = quoteStatus;
    footEl.appendChild(status);
  }

  // ── Email (asked for here because Shopify keeps nothing of a guest who leaves) ──
  function renderContact() {
    var wrap = document.createElement('div');
    wrap.className = 'cart-contact';

    var label = document.createElement('label');
    label.className = 'cart-contact__label';
    label.setAttribute('for', 'te-cart-email');
    label.textContent = 'Your email';
    wrap.appendChild(label);

    var input = document.createElement('input');
    input.type = 'email';
    input.id = 'te-cart-email';
    input.className = 'cart-contact__input';
    input.autocomplete = 'email';
    input.setAttribute('inputmode', 'email');
    input.placeholder = 'you@company.co.uk';
    input.required = true;
    input.value = contactEmail;
    input.setAttribute('aria-describedby', 'te-cart-email-note te-cart-email-error');
    input.addEventListener('input', function () {
      contactEmail = input.value.trim();
      setContactError('');
    });
    wrap.appendChild(input);

    var error = document.createElement('p');
    error.className = 'cart-contact__error';
    error.id = 'te-cart-email-error';
    error.hidden = true;
    wrap.appendChild(error);

    var note = document.createElement('p');
    note.className = 'cart-contact__note';
    note.id = 'te-cart-email-note';
    note.textContent = 'For your order confirmation. If you leave without paying, we’ll send one reminder about this basket.';
    wrap.appendChild(note);

    var opt = document.createElement('label');
    opt.className = 'cart-contact__optout';
    var box = document.createElement('input');
    box.type = 'checkbox';
    box.id = 'te-cart-noreminder';
    box.checked = noReminder;
    box.addEventListener('change', function () {
      noReminder = box.checked;
    });
    opt.appendChild(box);
    opt.appendChild(document.createTextNode('Don’t send me a reminder'));
    wrap.appendChild(opt);

    return wrap;
  }

  function setContactError(message) {
    var error = document.getElementById('te-cart-email-error');
    var input = document.getElementById('te-cart-email');
    if (error) {
      error.textContent = message;
      error.hidden = !message;
    }
    if (input) input.setAttribute('aria-invalid', message ? 'true' : 'false');
  }

  /** The typed email if it looks real (and remembers it); otherwise flags the box. */
  function requireEmail() {
    var input = document.getElementById('te-cart-email');
    var value = (input ? input.value : contactEmail).trim();
    if (!EMAIL_RE.test(value)) {
      setContactError(value ? 'That email doesn’t look right. Please check it.' : 'Please enter your email to continue.');
      if (input) input.focus();
      return null;
    }
    contactEmail = value.toLowerCase();
    setStoredEmail(contactEmail);
    return contactEmail;
  }

  function setStatus(message, isError) {
    var el = document.getElementById('te-cart-status');
    if (!el) return;
    el.textContent = message;
    el.classList.toggle('cart-contact__status--error', Boolean(isError));
  }

  function sendQuote(button, value) {
    var data = getStored();
    if (!data || !data.id || !selectedDelivery) return;
    var email = requireEmail();
    if (!email) return;
    button.disabled = true;
    button.textContent = 'Sending…';
    setStatus('', false);
    fetch(API_BASE + '/cart/quote', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ draftOrderId: data.id, method: selectedDelivery, email: email, noReminder: noReminder }),
    })
      .then(function (r) { return r.json().then(function (body) { return { ok: r.ok, body: body }; }); })
      .then(function (result) {
        if (!result.ok) {
          throw new Error((result.body && result.body.error) || 'Could not send the quote.');
        }
        quoteStatus = 'Quote sent to ' + result.body.sentTo + '. Check your inbox, and your junk folder just in case.';
        setStatus(quoteStatus, false);
        button.textContent = 'Quote sent ✓';
        try {
          if (typeof window.teTrack === 'function') window.teTrack('lead', { value: value });
        } catch (_e) {}
      })
      .catch(function (err) {
        button.disabled = false;
        button.textContent = 'Email me a quote';
        setStatus(err.message || 'Could not send the quote.', true);
      });
  }

  function startCheckout(button) {
    var data = getStored();
    if (!data || !data.id || !selectedDelivery) return;
    var email = requireEmail();
    if (!email) return;
    button.disabled = true;
    button.textContent = 'Going to checkout…';
    fetch(API_BASE + '/cart/delivery', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ draftOrderId: data.id, method: selectedDelivery, email: email, noReminder: noReminder }),
    })
      .then(function (r) { return r.json().then(function (body) { return { ok: r.ok, body: body }; }); })
      .then(function (result) {
        if (!result.ok || !result.body.invoiceUrl) {
          throw new Error((result.body && result.body.error) || 'Could not set delivery.');
        }
        try {
          if (typeof window.gtag === 'function') {
            window.gtag('event', 'begin_checkout', { currency: 'GBP' });
          } else if (Array.isArray(window.dataLayer)) {
            window.dataLayer.push({ event: 'begin_checkout', currency: 'GBP' });
          }
        } catch (_e) {}
        window.location.href = result.body.invoiceUrl;
      })
      .catch(function (err) {
        button.disabled = false;
        button.textContent = 'Checkout';
        alert(err.message || 'Could not set delivery.');
      });
  }

  // ── Remove an item ───────────────────────────────────────────────────────
  function removeItem(lineItemId, el) {
    var data = getStored();
    if (!data || !data.id) return;

    el.classList.add('cart-item--removing');
    quoteStatus = null; // a quote sent before this change no longer matches the basket

    fetch(API_BASE + '/cart/remove', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ draftOrderId: data.id, lineItemId: lineItemId }),
    })
      .then(function (r) { return r.json(); })
      .then(function (result) {
        if (result.itemCount === 0) {
          clearStored();
          updateBadge(0);
          renderEmpty();
        } else {
          setStored(result.draftOrderId, result.itemCount);
          updateBadge(result.itemCount);
          // Re-fetch the full cart to get updated totals.
          loadCart();
        }
      })
      .catch(function () {
        el.classList.remove('cart-item--removing');
      });
  }

  // ── Toast notification ───────────────────────────────────────────────────
  function showToast(message) {
    // Remove any existing toast.
    var old = document.querySelector('.te-toast');
    if (old) old.remove();

    var toast = document.createElement('div');
    toast.className = 'te-toast';
    toast.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true">' +
      '<path d="M20 6L9 17l-5-5"></path>' +
      '</svg>' +
      '<span>' + message + '</span>';

    toast.addEventListener('click', function () {
      toast.remove();
      openDrawer();
    });

    document.body.appendChild(toast);

    setTimeout(function () {
      toast.classList.add('te-toast--out');
      setTimeout(function () { toast.remove(); }, 300);
    }, 3500);
  }

  // ── Public API (used by calculator.js) ───────────────────────────────────
  window.TECart = {
    /** Open the cart drawer. */
    open: openDrawer,

    /** Close the cart drawer. */
    close: closeDrawer,

    /** Get the stored draft order ID (or null). */
    getDraftOrderId: function () {
      var d = getStored();
      return d ? d.id : null;
    },

    /**
     * Called by calculator.js after a successful /cart/add response.
     * Updates localStorage, badge, and shows a toast.
     */
    onItemAdded: function (draftOrderId, itemCount, productTitle) {
      setStored(draftOrderId, itemCount);
      updateBadge(itemCount);
      showToast((productTitle || 'Item') + ' added to cart');
    },

    /** Force-clear the cart (e.g. after checkout). */
    clear: function () {
      clearStored();
      updateBadge(0);
      if (isOpen) renderEmpty();
    },
  };
})();
